import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Box, Typography, useMediaQuery, useTheme,
  Divider, Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Dashboard,
  People,
  Security,
  Lock,
  Store,
  Folder as CategoryIcon,
  Label as AttributeIcon,
  LocalShipping,
  Receipt,
  ExpandLess,
  ExpandMore,
  Inventory2 as InventoryIcon,
  PeopleAlt as CustomersIcon,
  LocalOffer as PromotionsIcon,
  ShoppingCart as OrdersIcon,
  MoveToInbox as StockIcon,
  Public as OnlineOrdersIcon,
  Language as WebsiteIcon,
  RateReview as ReviewsIcon,
  Autorenew as ReturnsIcon,
  CurrencyExchange as CurrencyIcon,
  DeliveryDining as DeliveryIcon,
} from '@mui/icons-material';

const DRAWER_WIDTH = 240;

const Sidebar = ({ mobileOpen, onMobileClose }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const { t }     = useTranslation();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));
  const { user }  = useSelector((state) => state.auth);

  const [expandedSections, setExpandedSections] = useState({
    main: true,
    access: true,
    stores: true,
    products: true,
    purchases: true,
    sales: true,
    settings: true,
  });

  const isSuperAdmin = user?.globalRole === 'SUPER_ADMIN';

  const hasPermission = (permission) => {
    if (isSuperAdmin) return true;
    const has = (list) => list?.some((p) => p.name === permission || p.name === permission.split('.')[0] + '.*');
    return has(user?.rolePermissions) || has(user?.directPermissions);
  };

  const handleToggleSection = (section) =>
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const menuSections = [
    {
      id: 'main',
      titleKey: 'nav.main',
      items: [
        { textKey: 'nav.dashboard', icon: <Dashboard />, path: '/dashboard', permission: 'dashboard.read' },
      ],
    },
    {
      id: 'access',
      titleKey: 'nav.accessManagement',
      items: [
        { textKey: 'nav.users',       icon: <People />,   path: '/users',       permission: 'users.read' },
        { textKey: 'nav.roles',       icon: <Security />, path: '/roles',       permission: 'roles.read' },
        { textKey: 'nav.permissions', icon: <Lock />,     path: '/permissions', permission: 'permissions.read' },
      ],
    },
    {
      id: 'stores',
      titleKey: 'nav.storeManagement',
      items: [
        { textKey: 'nav.stores', icon: <Store />, path: '/stores', permission: 'stores.read' },
      ],
    },
    {
      id: 'products',
      titleKey: 'nav.productCatalog',
      items: [
        { textKey: 'nav.categories',     icon: <CategoryIcon />,  path: '/categories',     permission: 'categories.read' },
        { textKey: 'nav.attributes',      icon: <AttributeIcon />, path: '/attributes',      permission: 'attributes.read' },
        { textKey: 'nav.products',        icon: <InventoryIcon />, path: '/products',        permission: 'products.read' },
        { textKey: 'nav.stockMovements',  icon: <StockIcon />,     path: '/stock-movements', permission: 'stock.read' },
      ],
    },
    {
      id: 'purchases',
      titleKey: 'nav.purchasing',
      items: [
        { textKey: 'nav.suppliers',      icon: <LocalShipping />, path: '/suppliers',      permission: 'suppliers.read' },
        // Real seeded permission name is "purchase_orders.read" (underscore) —
        // this previously said "purchase-orders.read" (hyphen), which never
        // matched anything, so this item was invisible to every non-SUPER_ADMIN
        // user. See DatabaseInitializer#createPermissions.
        { textKey: 'nav.purchaseOrders', icon: <Receipt />,       path: '/purchase-orders',permission: 'purchase_orders.read' },
      ],
    },
    {
      id: 'sales',
      titleKey: 'nav.sales',
      items: [
        { textKey: 'nav.orders',     icon: <OrdersIcon />,    path: '/orders',     permission: 'orders.read' },
        { textKey: 'nav.shopOrders', icon: <OnlineOrdersIcon />, path: '/shop-orders', permission: 'shop_orders.read' },
        { textKey: 'nav.customers',  icon: <CustomersIcon />, path: '/customers',  permission: 'customers.read' },
        { textKey: 'nav.promotions', icon: <PromotionsIcon />,path: '/promotions', permission: 'promotions.read' },
        { textKey: 'nav_ext.returns', icon: <ReturnsIcon />,  path: '/returns',    permission: 'orders.read' },
        { textKey: 'nav_ext.reviews', icon: <ReviewsIcon />,  path: '/reviews',    permission: null, superAdminOnly: true },
      ],
    },
    // Website / currency settings are system-wide (not store-scoped) with no seeded
    // permission, so they're gated on globalRole (`superAdminOnly`) rather than
    // through hasPermission. Livraison uses the seeded stores.read permission.
    {
      id: 'settings',
      titleKey: 'nav_ext.settings',
      items: [
        { textKey: 'nav_ext.websiteSettings', icon: <WebsiteIcon />, path: '/settings/website', permission: null, superAdminOnly: true },
        { textKey: 'nav_ext.customOrders', icon: <WebsiteIcon />, path: '/settings/custom-orders', permission: null, superAdminOnly: true },
        { textKey: 'nav_ext.systemSettings', icon: <CurrencyIcon />, path: '/settings/system', permission: null, superAdminOnly: true },
        { textKey: 'nav_ext.delivery', icon: <DeliveryIcon />, path: '/delivery', permission: 'stores.read' },
        { textKey: 'nav_ext.deliveryCompanies', icon: <DeliveryIcon />, path: '/delivery-companies', permission: 'stores.read' },
      ],
    },
  ];

  const filterItems = (items) =>
    items.filter((item) => {
      if (item.superAdminOnly && !isSuperAdmin) return false;
      return !item.permission || hasPermission(item.permission);
    });

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 1 }}>
          INVOTRIX
        </Typography>
      </Toolbar>

      <Divider />

      <List sx={{ flexGrow: 1, py: 1, overflow: 'auto' }}>
        {menuSections.map((section) => {
          const visibleItems = filterItems(section.items);
          if (visibleItems.length === 0) return null;
          const expanded = expandedSections[section.id];

          return (
            <Box key={section.id}>
              <ListItemButton
                onClick={() => handleToggleSection(section.id)}
                sx={{
                  mx: 1, mb: 0.5, borderRadius: 1,
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <ListItemText
                  primary={t(section.titleKey)}
                  primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}
                />
                {expanded ? <ExpandLess sx={{ color: 'text.secondary' }} /> : <ExpandMore sx={{ color: 'text.secondary' }} />}
              </ListItemButton>

              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {visibleItems.map((item) => (
                    <ListItemButton
                      key={item.path}
                      selected={isActive(item.path)}
                      onClick={() => { navigate(item.path); if (isMobile) onMobileClose(); }}
                      sx={{
                        mx: 1, mb: 0.5, pl: 3, borderRadius: 1, color: 'text.primary',
                        '&.Mui-selected': {
                          backgroundColor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.16 : 0.08),
                          color: 'primary.main',
                          '& .MuiListItemIcon-root': { color: 'primary.main' },
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.24 : 0.12),
                        },
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={t(item.textKey)}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive(item.path) ? 600 : 400 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('common.version')} 1.0.0
        </Typography>
      </Box>
    </Box>
  );

  const drawerPaperSx = {
    boxSizing: 'border-box',
    width: DRAWER_WIDTH,
    backgroundColor: 'background.default',
    borderRight: (t) => `1px solid ${t.palette.divider}`,
  };

  return (
    <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': drawerPaperSx }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': drawerPaperSx }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
